import React from "react";
import { useParams } from "react-router";
import { FormattedNumber, FormattedDate } from "react-intl";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Form } from "react-final-form";
import { useAuth } from "@syos-music/auth";
import {
  TextField,
  SelectField,
  Fieldset,
  CheckboxGroupField,
} from "@syos-music/form";

import {
  Container,
  Grid,
  Fab,
  MenuItem,
  List,
  ListSubheader,
  ListItemText,
  ListItem,
  ListItemSecondaryAction,
  Typography,
} from "@mui/material";
import { AccountSwitch as ImpersonateIcon } from "mdi-material-ui";

import Layout from "components/common/Layout";
import SaveFab from "components/common/SaveFab";
import WebSiteLink from "components/common/WebSiteLink";
import UserListItem from "components/user/UserListItem";

// prettier-ignore
const ROLES = {
  ADMIN:          { label: "Admin" },
  SYOS_DESIGNER:  { label: "Designer Syos" },
  SYOS_MEMBER:    { label: "Membre Syos" },
  TRUSTED_SELLER: { label: "Revendeur officiel" },
  AFFILIATED:     { label: "Affilié" },
  ARTIST:         { label: "Artiste" },
};

const FRAGMENTS = {
  user: gql`
    fragment Admin_ProfilePage_User on User {
      id

      profile {
        email
        firstName
        lastName
        fullName
        locale
      }

      roles

      affiliation {
        incomeRatio
      }

      referral {
        id
        points

        referrer {
          id

          ...UserListItem_User
        }

        friends {
          count
          edges {
            cursor
            node {
              id

              ...UserListItem_User
            }
          }
        }

        claimedRewards {
          count
          edges {
            cursor
            node {
              id
              createdAt
              reward {
                id
                points
                label {
                  fr
                }
              }
            }
          }
        }
      }
    }

    ${UserListItem.fragments.user}
  `,
};

const QUERY = gql`
  query ($userId: ID!) {
    user(id: $userId) {
      id

      ...Admin_ProfilePage_User
    }
  }

  ${FRAGMENTS.user}
`;

const MUTATION = gql`
  mutation (
    $id: ID!
    $profile: UpdateUser_Profile!
    $roles: [UserRole]
    $affiliation: UpdateUser_Affiliation
    $referral: UpdateUser_Referral!
  ) {
    UpdateUser(
      userId: $id
      profile: $profile
      roles: $roles
      affiliation: $affiliation
      referral: $referral
    ) {
      user {
        id

        ...Admin_ProfilePage_User
      }
    }
  }

  ${FRAGMENTS.user}
`;

export default function ProfilePage() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole("ADMIN");

  const { userId } = useParams();

  const [updateUser] = useMutation(MUTATION);

  const { data, loading } = useQuery(QUERY, { variables: { userId } });
  if (loading) return <Layout.Loading />;
  if (!data?.user) return <Layout.NotFound />;

  const initialValues = {
    profile: {
      email: data.user.profile.email,
      locale: data.user.profile.locale,
      firstName: data.user.profile.firstName,
      lastName: data.user.profile.lastName,
    },
    referral: {
      points: data.user.referral.points,
    },
  };

  if (isAdmin) {
    initialValues.roles = data.user.roles;
  }

  if (isAdmin && data.user.roles.includes("AFFILIATED")) {
    initialValues.affiliation = {
      incomeRatio: data.user.affiliation?.incomeRatio,
    };
  }

  return (
    <Form
      onSubmit={(values) =>
        updateUser({
          variables: {
            id: data.user.id,
            ...values,
          },
        })
          .then(() => {})
          .catch(() => true)
      }
      initialValues={initialValues}
      keepDirtyOnReinitialize
    >
      {({ handleSubmit, pristine, submitting, values }) => (
        <form onSubmit={handleSubmit} noValidate autoComplete="off">
          <Layout.Toolbar fixed>
            <Grid container alignItems="center">
              <Grid item xs />

              {isAdmin && (
                <Grid item>
                  <Fab
                    size="small"
                    component={WebSiteLink}
                    to={`/auth/login?impersonation=${userId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ImpersonateIcon color="inherit" />
                  </Fab>
                </Grid>
              )}

              <Grid item>
                <SaveFab
                  onSave={handleSubmit}
                  disabled={pristine}
                  loading={submitting}
                />
              </Grid>
            </Grid>
          </Layout.Toolbar>

          <Container fixed sx={{ p: 2 }}>
            <Fieldset legend="Profile" sx={{ mb: 2 }}>
              <Grid container>
                <Grid item xs={12} md={8}>
                  <TextField
                    name="profile.email"
                    type="email"
                    required
                    label="Email"
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <SelectField name="profile.locale" label="Langue">
                    <MenuItem value="fr">Français</MenuItem>
                    <MenuItem value="en">Anglais</MenuItem>
                  </SelectField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField name="profile.firstName" required label="Prénom" />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField name="profile.lastName" required label="Nom" />
                </Grid>
              </Grid>
            </Fieldset>

            {isAdmin && (
              <Fieldset legend="Roles" sx={{ mb: 2 }}>
                <CheckboxGroupField name="roles">
                  {Object.entries(ROLES).map(([name, { label }]) => (
                    <CheckboxGroupField.Option
                      key={name}
                      name={name}
                      label={label}
                    />
                  ))}
                </CheckboxGroupField>
              </Fieldset>
            )}

            {isAdmin && values.roles.includes("AFFILIATED") && (
              <Fieldset legend="Affiliation" sx={{ mb: 2 }}>
                <TextField
                  type="number"
                  name="affiliation.incomeRatio"
                  InputProps={{
                    inputProps: {
                      min: 0,
                      max: 1,
                      step: 0.01,
                    },
                  }}
                  parse={(value) => parseFloat(value)}
                  label="Ratio des revenus"
                  helperText={
                    <FormattedNumber
                      value={values.affiliation?.incomeRatio}
                      // eslint-disable-next-line react/style-prop-object
                      style="percent"
                    />
                  }
                />
              </Fieldset>
            )}

            <Fieldset legend="Parrainage" sx={{ mb: 2 }}>
              <Grid container>
                <Grid item xs={12}>
                  <TextField
                    name="referral.points"
                    type="number"
                    label="Points"
                    InputProps={{
                      inputProps: { step: 1, min: 1 },
                    }}
                    parse={(value) => {
                      const result = parseInt(value, 10);
                      return isNaN(result) ? value : result;
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <List disablePadding sx={{ m: (t) => t.spacing(0, -2) }}>
                    <ListSubheader>
                      <ListItemText
                        primary="Parrain"
                        primaryTypographyProps={{
                          variant: "h4",
                          color: "primary",
                        }}
                      />
                    </ListSubheader>

                    {data.user.referral.referrer ? (
                      <UserListItem user={data.user.referral.referrer} />
                    ) : (
                      <ListItem dense>
                        <ListItemText primary="Pas de parrain" />
                      </ListItem>
                    )}
                  </List>

                  <List disablePadding sx={{ m: (t) => t.spacing(0, -2) }}>
                    <ListSubheader>
                      <ListItemText
                        primary="Filleuls"
                        primaryTypographyProps={{
                          variant: "h4",
                          color: "primary",
                        }}
                      />
                    </ListSubheader>

                    {data.user.referral.friends.count > 0 ? (
                      data.user.referral.friends.edges.map(
                        ({ cursor, node }) => (
                          <UserListItem key={cursor} user={node} />
                        )
                      )
                    ) : (
                      <ListItem dense>
                        <ListItemText primary="Aucun filleul" />
                      </ListItem>
                    )}
                  </List>

                  <List disablePadding sx={{ m: (t) => t.spacing(0, -2) }}>
                    <ListSubheader>
                      <ListItemText
                        primary="Récompenses"
                        primaryTypographyProps={{
                          variant: "h4",
                          color: "primary",
                        }}
                      />
                    </ListSubheader>

                    {data.user.referral.claimedRewards.count > 0 ? (
                      data.user.referral.claimedRewards.edges.map(
                        ({ cursor, node }) => (
                          <ListItem key={cursor} dense>
                            <ListItemText
                              primary={node.reward.label["fr"]}
                              secondary={
                                <small>
                                  <FormattedDate
                                    value={node.createdAt}
                                    year="numeric"
                                    month="long"
                                    day="2-digit"
                                    hour="2-digit"
                                    minute="2-digit"
                                  />
                                </small>
                              }
                            />

                            <ListItemSecondaryAction>
                              <Typography color="textSecondary">
                                {-node.reward.points} points
                              </Typography>
                            </ListItemSecondaryAction>
                          </ListItem>
                        )
                      )
                    ) : (
                      <ListItem dense>
                        <ListItemText primary="Aucunne récompense" />
                      </ListItem>
                    )}
                  </List>
                </Grid>
              </Grid>
            </Fieldset>
          </Container>
        </form>
      )}
    </Form>
  );
}
